import { COORD_LIMIT, formatLength, parseLength, UNITS, UNIT_NAMES, type LengthUnit, type NodeRecord, type Paint } from '@flobi/artwork-core';
import { useState } from 'react';
import { Icon } from '../chrome/icons';
import { type ArtboardPreset } from '../useEditorCanvas';
import { nameOfSize, presetMenu, presetOfSize, sizedLike } from '../artboardPresets';
import { MenuWell } from './MenuWell';
import { Button, NumberField, PopUpButton, Row, Section, SegmentedControl } from '../../ui/controls';
import { Menu, useMenu } from '../../ui/menu';
import { tip } from '../../ui/tooltip';
import { PaintControl } from './Paint';

/**
 * Nothing chosen: the page you are on — its size and its background, which are the first
 * two things anybody sets — and the other pages to go to. Keynote's inspector does the
 * same with nothing selected, and for the same reason: an empty selection is still a
 * question about something, and the something is the page.
 *
 * Everything the file is measured in and everything you are shown while you work — units,
 * resolution, grids, guides, snapping — is not about the page, so it is not here: the
 * settings are in Document Settings and the view is in the View menu, both off the title.
 */

type Artboard = NodeRecord & { width?: number; height?: number; fills?: Paint[] };

export function PageInspector({
  artboard,
  artboards,
  units,
  onUpdate,
  onFocus,
  onAdd,
  showTitle = true,
  showNavigation = true,
}: {
  showTitle?: boolean;
  showNavigation?: boolean;
  /** The page on screen, or null when there are none yet. */
  artboard: Artboard | null;
  artboards: { id: string; name: string; current: boolean }[];
  units: LengthUnit;
  onUpdate: (id: string, props: Record<string, unknown>) => void;
  onFocus: (id: string) => void;
  onAdd: (preset: ArtboardPreset) => void;
}) {
  const add = useMenu();
  const addMenu = (
    <Menu
      open={add.open}
      anchor={add.anchor}
      onClose={add.close}
      placement="left"
      label="Add Artboard"
      width={260}
      items={presetMenu((preset) => onAdd(preset.id))}
    />
  );

  if (!artboard) {
    return (
      <Section title="New Artboard" collapsible={false}>
        <MenuWell well={{ icon: 'add-artboard', title: 'New Artboard — choose a size', value: 'Choose a Size', items: presetMenu((preset) => onAdd(preset.id)) }} placement="below" wide />
      </Section>
    );
  }

  const width = artboard.width ?? 0;
  const height = artboard.height ?? 0;
  const length = {
    format: (value: number) => formatLength(value, units, { bare: true }),
    parse: (text: string) => parseLength(text, units),
    suffix: units === 'ft-in' ? undefined : units,
    decimals: 2,
  };

  return (
    <>
      {showTitle && <PageName name={artboard.name || 'Artboard'} onRename={(name) => onUpdate(artboard.id, { name })} />}

      <Section title="Artboard" id="context-artboard">
        <MenuWell
          well={{
            icon: 'artboard',
            title: 'Size — a preset, kept the same way up',
            value: nameOfSize(width, height),
            items: presetMenu((one) => onUpdate(artboard.id, { ...sizedLike(one, width, height), preset: one.id }), presetOfSize(width, height)?.preset.id),
          }}
          placement="below"
          wide
        />
        <div className="grid grid-cols-2 gap-[6px]">
          {/* Held to the page's own limit: a larger number was refused without a word and
              the field snapped back (QA 2026-09-24, artboards-F16). */}
          <NumberField label="Width" prefix="W" value={width} min={1} max={COORD_LIMIT} {...length} onChange={(value) => onUpdate(artboard.id, { width: value })} />
          <NumberField label="Height" prefix="H" value={height} min={1} max={COORD_LIMIT} {...length} onChange={(value) => onUpdate(artboard.id, { height: value })} />
        </div>
      </Section>

      <Section title="Background" id="context-background">
        <PaintControl label="Fill" paint={artboard.fills?.[0]} onChange={(paint) => onUpdate(artboard.id, { fills: [paint] })} onClear={() => onUpdate(artboard.id, { fills: [] })} />
      </Section>

      {showNavigation && <Section
        title="Artboards"
        collapsible={false}
        accessory={
          <>
            <Button ref={add.ref} size="small" variant="borderless" icon="plus" aria-haspopup="menu" aria-expanded={add.open} onClick={add.toggle}>
              Add
            </Button>
            {addMenu}
          </>
        }
      >
        {artboards.length > 1 ? (
          <div className="grid grid-cols-[minmax(0,1fr)] gap-[1px]">
            {artboards.map((board) => (
              <button
                key={board.id}
                type="button"
                onClick={() => onFocus(board.id)}
                aria-current={board.current ? 'true' : undefined}
                className={`flex h-[28px] items-center gap-[8px] rounded-[8px] px-[8px] text-left text-[12px] transition-colors ${
                  board.current ? 'bg-fill-selected text-label' : 'text-label hover:bg-fill-2'
                }`}
              >
                <span className="text-label-2">
                  <Icon name="artboard" size={14} solid={board.current} />
                </span>
                <span className="min-w-0 flex-1 truncate">{board.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </Section>}
    </>
  );
}

/** The page's name as the panel's title; a double-click renames it, as a layer's does. */
function PageName({ name, onRename }: { name: string; onRename: (name: string) => void }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="px-[14px] pb-[4px] pt-[14px]">
      {editing ? (
        <input
          autoFocus
          aria-label="Artboard name"
          defaultValue={name}
          onFocus={(event) => event.currentTarget.select()}
          onBlur={(event) => {
            const next = event.target.value.trim();
            if (next && next !== name) onRename(next);
            setEditing(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') setEditing(false);
            event.stopPropagation();
          }}
          className="h-[26px] w-full rounded-[7px] bg-fill-1 px-[7px] text-[15px] font-semibold text-label outline-none"
        />
      ) : (
        <h2 onDoubleClick={() => setEditing(true)} className="truncate text-[15px] font-semibold text-label">
          {name}
        </h2>
      )}
    </div>
  );
}

// ── document settings ───────────────────────────────────────────────────────

const RESOLUTIONS = [72, 150, 300, 600];

/**
 * What the file is measured in, what a PNG of it comes out at, how far apart the grid is
 * and what it is made for — set once per file, so they live in a popover off the title
 * rather than in a panel that is on screen all day.
 *
 * Colour Mode: RGB is for screens; CMYK is for a press (FOGRA39, ISO Coated v2), and the
 * canvas, every swatch and every export then show colours as they will print, and colours
 * can be typed as ink. The press is the row's value, only when there is one.
 */
export function DocumentSettings({
  units,
  resolution,
  grid,
  colorMode,
  press,
  onUnits,
  onResolution,
  onGrid,
  onColorMode,
}: {
  units: LengthUnit;
  resolution: number;
  grid: { step: number; divisions: number };
  colorMode: 'rgb' | 'cmyk';
  /** The press profile's own name, once it has loaded. */
  press: string | null;
  onUnits: (units: LengthUnit) => void;
  onResolution: (ppi: number) => void;
  onGrid: (patch: { step?: number; divisions?: number }) => void;
  onColorMode: (mode: 'rgb' | 'cmyk') => void;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[10px] p-[14px]">
      <h2 className="text-[13px] font-semibold text-label">Document Settings</h2>
      <Row label="Colour">
        <SegmentedControl
          className="w-[116px] shrink-0"
          size="small"
          label="Colour mode"
          value={colorMode}
          onChange={onColorMode}
          segments={[
            { value: 'rgb', label: 'RGB', tip: 'RGB — for screens' },
            { value: 'cmyk', label: 'CMYK', tip: 'CMYK — for print: colours show as they will print' },
          ]}
        />
        {colorMode === 'cmyk' ? (
          <span className="min-w-0 flex-1 truncate text-right text-[12px] text-label-2" {...tip(`Press: ${press ?? 'ISO Coated v2 (ECI)'}`)}>
            FOGRA39
          </span>
        ) : null}
      </Row>
      <Row label="Units">
        <PopUpButton className="min-w-0 flex-1" label="Units" value={units} onChange={onUnits} options={UNITS.map((unit) => [unit, UNIT_NAMES[unit]] as const)} />
      </Row>
      <Row label="Resolution" hint="What a PNG of the drawing comes out at, in pixels per inch">
        <SegmentedControl
          className="min-w-0 flex-1"
          size="small"
          label="Resolution, pixels per inch"
          value={RESOLUTIONS.includes(resolution) ? String(resolution) : null}
          onChange={(value) => onResolution(Number(value))}
          segments={RESOLUTIONS.map((ppi) => ({ value: String(ppi), label: String(ppi) }))}
        />
      </Row>
      <Row label="Grid">
        <NumberField
          className="min-w-0 flex-1"
          label="Gridline every"
          value={grid.step}
          min={0.01}
          format={(value) => formatLength(value, units, { bare: true })}
          parse={(text) => parseLength(text, units)}
          suffix={units === 'ft-in' ? undefined : units}
          onChange={(step) => step > 0 && onGrid({ step })}
        />
        <NumberField className="w-[60px]" label="Subdivisions" prefix="÷" value={grid.divisions} min={1} max={100} decimals={0} onChange={(divisions) => onGrid({ divisions: Math.round(divisions) })} />
      </Row>
    </div>
  );
}
